import { serializeCss } from '@precis-digital/kurama/utils'
import { type CSSObject } from '@precis-digital/kurama/theme'
import { type CopilotHighlightRingKind } from './copilotElementHighlight'

/**
 * Serializes Emotion CSS objects into plain CSS text for injected stylesheets.
 * Used when bootstrapping highlight animation styles into the document head.
 */
export const serializeCopilotHighlightCss = serializeCss

export const COPILOT_HIGHLIGHT_RING_MASK: CSSObject = {
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  maskComposite: 'exclude'
}

interface BuildCopilotHighlightRingBeforeStylesOptions {
  highlightAttribute: string
  ringVariablePrefix: CopilotHighlightRingKind
  ringWidthPx: number
  background: string
  extraStyles?: CSSObject
}

/**
 * Builds shared `::before` pseudo-element styles for copilot highlight rings.
 * Applies the ring mask, inset, border radius, and gradient background.
 */
export const buildCopilotHighlightRingBeforeStyles = ({
  highlightAttribute,
  ringVariablePrefix,
  ringWidthPx,
  background,
  extraStyles = {}
}: BuildCopilotHighlightRingBeforeStylesOptions): CSSObject => ({
  [`[${highlightAttribute}]::before`]: {
    content: '""',
    position: 'absolute',
    inset: `calc(-1 * var(--copilot-${ringVariablePrefix}-ring-outset, ${ringWidthPx}px))`,
    borderRadius: `calc(var(--copilot-${ringVariablePrefix}-border-radius, 0px) + var(--copilot-${ringVariablePrefix}-ring-outset, ${ringWidthPx}px))`,
    padding: `var(--copilot-${ringVariablePrefix}-ring-width, ${ringWidthPx}px)`,
    backgroundImage: background,
    ...extraStyles,
    ...COPILOT_HIGHLIGHT_RING_MASK,
    pointerEvents: 'none',
    zIndex: 1
  }
})

/**
 * Injects or updates a `<style>` tag in the document head for copilot highlight animations.
 * Reuses an existing style element when the same `styleId` is already present.
 */
export const injectCopilotStylesheet = (styleId: string, styles: CSSObject): void => {
  if (typeof document === 'undefined') return

  const cssText = serializeCopilotHighlightCss(styles)
  const existingStyle = document.getElementById(styleId)

  if (existingStyle != null) {
    existingStyle.textContent = cssText
    return
  }

  const style = document.createElement('style')
  style.id = styleId
  style.textContent = cssText
  document.head.appendChild(style)
}
